import { useMemo, useState } from 'react'
import {
  Button,
  Card,
  ErrorState,
  LoadingState,
  PageHeader,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@/components/ui'
import { MatrixTab } from '@/pages/it-structures/tabs/MatrixTab.tsx'
import { UserGroupMatrix } from '@/pages/it-structures/tabs/UserGroupMatrix.tsx'
import { DepartmentGroupMatrix } from '@/pages/it-structures/tabs/DepartmentGroupMatrix.tsx'
import {
  CUSTOMER_CAPS,
  MatrixCapabilitiesProvider,
} from '@/pages/it-structures/tabs/matrix/MatrixCapabilities.tsx'
import type { GroupType } from '@/pages/it-structures/types.ts'
import { useCustomerStructure } from '../hooks/useCustomerStructure'

const DEPT_MATRIX_TYPES: GroupType[] = ['LI', 'FU', 'SW']
const USER_MATRIX_TYPES: GroupType[] = ['AB']

export function CustomerAccessMatrixPage() {
  const {
    data,
    loading,
    loadError,
    dirty,
    saving,
    save,
    update,
    lastViolations,
    lastError,
    lastSaveSummary,
    serverStructure,
    diagnostic,
  } = useCustomerStructure()
  const [notes, setNotes] = useState('')
  const [deptTypes, setDeptTypes] = useState<GroupType[]>(DEPT_MATRIX_TYPES)

  const saveSummary = useMemo(() => {
    if (!lastSaveSummary) return null
    const parts = [`v${lastSaveSummary.version} gespeichert (${lastSaveSummary.changes} Änderungen)`]
    if (lastSaveSummary.tanssTicketId) parts.push(`TANSS-Ticket #${lastSaveSummary.tanssTicketId}`)
    if (lastSaveSummary.ticketWarning) parts.push(`Warnung: ${lastSaveSummary.ticketWarning}`)
    return parts.join(' · ')
  }, [lastSaveSummary])

  if (loading) return <LoadingState />
  if (loadError) return <ErrorState details={loadError} />
  if (!data || !serverStructure) {
    return (
      <div>
        <PageHeader title="Zugriffsmatrix" />
        <Card>
          <Card.Body>
            <p className="text-body text-gray-600 mb-4">
              Für diesen Kunden ist noch keine Struktur hinterlegt. Bitte implec kontaktieren,
              damit wir die initiale Zugriffsmatrix für Sie aufsetzen.
            </p>
            {diagnostic && (
              <details className="mt-4 text-caption text-gray-600 bg-gray-50 border border-gray-200 rounded-md p-3">
                <summary className="cursor-pointer font-medium text-gray-700">Diagnose-Details</summary>
                <div className="mt-2 space-y-1">
                  {diagnostic.reason && (
                    <div><span className="font-medium">Grund:</span> {diagnostic.reason}</div>
                  )}
                  {diagnostic.requestedCustomerId && (
                    <div><span className="font-medium">Kunden-ID aus Login:</span> <code>{diagnostic.requestedCustomerId}</code></div>
                  )}
                  {diagnostic.email && (
                    <div><span className="font-medium">E-Mail:</span> {diagnostic.email}</div>
                  )}
                  {diagnostic.foundVersion != null && (
                    <div><span className="font-medium">Gefundene Version:</span> {diagnostic.foundVersion} (is_active={String(diagnostic.isActive)})</div>
                  )}
                  {diagnostic.existingCustomerIdsInTable && diagnostic.existingCustomerIdsInTable.length > 0 && (
                    <div>
                      <div className="font-medium">Kunden-IDs mit Einträgen in der Tabelle:</div>
                      <ul className="ml-4 list-disc">
                        {diagnostic.existingCustomerIdsInTable.map((id) => (
                          <li key={id}><code>{id}</code></li>
                        ))}
                      </ul>
                      <div className="mt-2 text-gray-500">
                        Stimmt die „Kunden-ID aus Login" mit keiner dieser IDs überein, wurde der
                        Portal-User im SysMan-Admin mit einem anderen Kunden-Datensatz verknüpft als
                        die Struktur. implec muss die Verknüpfung korrigieren.
                      </div>
                    </div>
                  )}
                </div>
              </details>
            )}
          </Card.Body>
        </Card>
      </div>
    )
  }

  return (
    <MatrixCapabilitiesProvider value={CUSTOMER_CAPS}>
      <PageHeader
        title="Zugriffsmatrix"
        description="Ordner, Verantwortliche und Berechtigungen für Ihre IT-Struktur. Laufwerke, Server und Freigaben kann nur implec ändern."
        actions={
          <Button variant="primary" size="sm" onClick={() => save(notes || undefined)} disabled={!dirty || saving}>
            {saving ? 'Speichere …' : dirty ? 'Änderungen speichern' : 'Keine Änderungen'}
          </Button>
        }
      />

      {saveSummary && (
        <div className="mb-4 p-3 rounded-md bg-green-50 border border-green-200 text-small text-green-900">
          {saveSummary}
        </div>
      )}

      {lastError && (
        <ErrorState
          className="mb-4"
          title={lastError}
          details={
            lastViolations && lastViolations.length > 0
              ? lastViolations.map((v) => `• [${v.opType}] ${v.detail}${v.ref ? ` (${v.ref})` : ''}`).join('\n')
              : undefined
          }
        />
      )}

      <Card>
        <Card.Body>
          <div className="text-caption text-gray-500 mb-3">
            Version: {serverStructure.version}
            {dirty ? ' · Nicht gespeicherte Änderungen' : ''}
          </div>
          <Tabs defaultValue="freigaben">
            <TabsList>
              <TabsTrigger value="freigaben">Freigabe-Matrix</TabsTrigger>
              <TabsTrigger value="users">User-Matrix</TabsTrigger>
              <TabsTrigger value="departments">Abteilungs-Matrix</TabsTrigger>
            </TabsList>
            <TabsContent value="freigaben" className="pt-4">
              <MatrixTab data={data} update={update} />
            </TabsContent>
            <TabsContent value="users" className="pt-4">
              <UserGroupMatrix
                data={data}
                update={update}
                groupTypes={USER_MATRIX_TYPES}
                activeTypes={USER_MATRIX_TYPES}
                onActiveTypesChange={() => {
                  /* nur Typ AB — kein Filter */
                }}
              />
            </TabsContent>
            <TabsContent value="departments" className="pt-4">
              <DepartmentGroupMatrix
                data={data}
                update={update}
                groupTypes={DEPT_MATRIX_TYPES}
                activeTypes={deptTypes}
                onActiveTypesChange={setDeptTypes}
              />
            </TabsContent>
          </Tabs>
        </Card.Body>
      </Card>

      <Card className="mt-4">
        <Card.Body>
          <Textarea
            label="Notiz für implec (optional)"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            hint="Erscheint im automatisch erzeugten TANSS-Ticket."
          />
          <div className="flex justify-end mt-3">
            <Button variant="primary" onClick={() => save(notes || undefined)} disabled={!dirty || saving}>
              {saving ? 'Speichere …' : 'Änderungen speichern'}
            </Button>
          </div>
        </Card.Body>
      </Card>
    </MatrixCapabilitiesProvider>
  )
}
